import { ITaskBlock } from '../blocks-core/ITaskBlock'
import { BlockEngine } from '../blocks-core/BlockEngine'
import { BlockComposer } from './BlockComposer'
import { Logger } from './Logger'
import { PythonRuntime } from './PythonRuntime'
import { StateStore } from './StateStore'
import { InterfaceConfig } from './types'
import { JSONObject } from '../../types/common.types'
import { PythonModule } from './PythonModule'

type TaskInterfaceOptions = {
    config: InterfaceConfig
    blockEngine?: BlockEngine
    initialState?: Record<string, unknown>
    inputData?: JSONObject
}

export class TaskInterface {
    private blockEngine: BlockEngine
    private config: InterfaceConfig
    private blockComposer: BlockComposer
    private stateStore: StateStore
    private pythonRuntime: PythonRuntime

    constructor({ config, blockEngine, initialState, inputData }: TaskInterfaceOptions) {
        this.config = config
        this.blockEngine = blockEngine || new BlockEngine()
        this.stateStore = new StateStore(initialState || {}, config.state, inputData)
        this.pythonRuntime = new PythonRuntime(this.stateStore, this.blockEngine)
        this.blockComposer = new BlockComposer(
            this.blockEngine,
            this.stateStore,
            this.pythonRuntime,
            inputData
        )
    }

    async build(): Promise<ITaskBlock[]> {
        if (this.config.pythonCode?.trim()) {
            await this.pythonRuntime.initialize(this.config.packages)
        }

        const blocks = this.blockComposer.compose(this.config, true)

        if (this.config.pythonCode?.trim()) {
            await this.pythonRuntime.execute(this.config.pythonCode)
        }

        this.blockComposer.updateEventHandlers()

        return blocks
    }

    getLogger(): Logger {
        return this.pythonRuntime.getLogger()
    }

    getBlockEngine(): BlockEngine {
        return this.blockEngine
    }

    getStateStore(): StateStore {
        return this.stateStore
    }

    getPythonRuntime(): PythonRuntime {
        return this.pythonRuntime
    }

    getPythonModule(): PythonModule {
        return this.pythonRuntime.getPythonModule()
    }

    destroy(): void {
        this.blockEngine.clear()
        this.pythonRuntime.destroy()
    }
}
